import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import { useContext, memo } from 'react';
import { DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE } from '../../constants';
import useUniqueId from 'react-utils/hooks/useUniqueId';
import { FieldsetContext } from '../../context/FieldsetContext';
import { isDateInRange, isValid } from '../../utils/SimpleDate';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { Controllable } from '../Controllable';
import ShareInput from '../ShareInput/ShareInput';
import { TextInput } from '../TextInput/TextInput';
import { getLocalizedDateFormat } from '../../utils/Locale';
import AbstractCalendar from '../AbstractCalendar/AbstractCalendar';
import { CalendarPagePopover } from '../Dates/CalendarPagePopover';
import { UsesToday } from '../Dates/internal/UsesToday';
import { InputIconWrapper } from '../Dates/internal/InputIconWrapper';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import I18n from 'I18n';
import { useComputedDateInputPlaceholder } from '../../../hooks/usePlaceholder';
import { useDisableFocusStyles } from '../../../hooks/useDisableFocusStyles';
import { DateIcon } from 'foundations-assets/icons/components/DateIcon';
import { jsx as _jsx } from "react/jsx-runtime";
const isValidValue = (value, min, max) => {
  if (value == null) return true;
  if (!isValid(value)) return false;
  return isDateInRange(value, min, max);
};
const DateInputComponent = props => {
  const {
      'aria-invalid': ariaInvalid,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-required': ariaRequired,
      autoFocus,
      calendarPage,
      clearLabel = I18n.text('ui.datePicker.clear'),
      disabled,
      disabledValues,
      error,
      focusedValue,
      format = 'L',
      iconSize,
      Input = TextInput,
      inputRef,
      inputWidth,
      min,
      // minValue,
      max,
      // maxValue,
      maxValidationMessage = I18n.text('ui.datePicker.dateAboveMax'),
      // maxValueValidationMessage,
      minValidationMessage = I18n.text('ui.datePicker.dateBelowMin'),
      // minValueValidationMessage,
      name,
      onCalendarPageChange,
      onChange,
      onFocusedValueChange: __onFocusedValueChange,
      onOpenChange,
      open = false,
      readOnly,
      required,
      today,
      todayLabel = I18n.text('ui.datePicker.today'),
      use,
      value,
      onFocus,
      disableFocusStyles: disableFocusStylesProp,
      [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: dataComponentAttribute
    } = props,
    rest = _objectWithoutPropertiesLoose(props, ["aria-invalid", "aria-label", "aria-labelledby", "aria-required", "autoFocus", "calendarPage", "clearLabel", "disabled", "disabledValues", "error", "focusedValue", "format", "iconSize", "Input", "inputRef", "inputWidth", "min", "max", "maxValidationMessage", "minValidationMessage", "name", "onCalendarPageChange", "onChange", "onFocusedValueChange", "onOpenChange", "open", "readOnly", "required", "today", "todayLabel", "use", "value", "onFocus", "disableFocusStyles", DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE].map(_toPropertyKey));
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const calendarId = useUniqueId('dateinput-calendar-');
  const popoverId = useUniqueId('dateinput-popover-');
  const fieldsetContext = useContext(FieldsetContext);
  const computedDisabled = disabled || fieldsetContext.disabled;
  const computedPlaceholder = useComputedDateInputPlaceholder(format);
  const disableFocusStyles = useDisableFocusStyles(disableFocusStylesProp);
  const triggerOpen = () => {
    if (readOnly || computedDisabled) return;
    if (!onOpenChange) return;
    onOpenChange(syntheticEvent(true));
  };
  return /*#__PURE__*/_jsx(AbstractCalendar, {
    calendarPage: calendarPage,
    disabledValues: disabledValues,
    format: format,
    min: min,
    max: max,
    onCalendarPageChange: onCalendarPageChange,
    onChange: onChange,
    onFocusedValueChange: __onFocusedValueChange,
    onOpenChange: onOpenChange,
    open: open,
    today: today,
    value: value,
    children: ({
      onInputChange,
      onKeyDown,
      onKeyUp,
      onBlur,
      inputValue
    }) => {
      // If fieldsetContext.size is 'small', icon should be 14x14, otherwise 16x16
      const computedIconSize = iconSize || (fieldsetContext.size === 'small' ? '200' : '300');
      return /*#__PURE__*/_jsx(CalendarPagePopover, {
        calendarId: calendarId,
        calendarPage: calendarPage,
        clearLabel: clearLabel,
        disabledValues: disabledValues,
        focusedValue: focusedValue,
        id: popoverId,
        max: max,
        min: min,
        maxValidationMessage: maxValidationMessage,
        minValidationMessage: minValidationMessage,
        onCalendarPageChange: onCalendarPageChange,
        onChange: onChange,
        onFocusLeave: () => {
          if (!onOpenChange) return;
          onOpenChange(syntheticEvent(false));
        },
        onMouseDown: e => {
          e.preventDefault();
        },
        onOpenChange: onOpenChange,
        open: open && !computedDisabled && !readOnly,
        today: today,
        todayLabel: todayLabel,
        value: value,
        [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: dataComponentAttribute || 'DateInput',
        children: /*#__PURE__*/_jsx(InputIconWrapper, Object.assign({}, domProps, {
          onFocus: onFocus,
          $cssStyles: cssStyles,
          disabled: computedDisabled,
          icon: DateIcon,
          size: computedIconSize,
          children: /*#__PURE__*/_jsx(Input, {
            "aria-invalid": error ? 'true' : ariaInvalid,
            "aria-label": ariaLabel,
            "aria-labelledby": ariaLabelledBy,
            "aria-required": ariaRequired,
            autoComplete: "off",
            autoFocus: autoFocus,
            "data-format": getLocalizedDateFormat(format),
            disabled: computedDisabled,
            error: error !== null && error !== void 0 ? error : !isValidValue(value, min, max),
            inputRef: inputRef,
            name: name,
            onBlur: onBlur,
            onChange: onInputChange,
            onClick: triggerOpen,
            onFocus: e => {
              if (readOnly || computedDisabled) return;
              e.target.select(); // inputRef is not yet available (see facebook/react#7769)
              triggerOpen();
            },
            onKeyDown: onKeyDown,
            onKeyUp: onKeyUp,
            placeholder: computedPlaceholder,
            readOnly: readOnly,
            required: required,
            use: use,
            value: inputValue,
            inlineSize: inputWidth ? `${inputWidth}px` : undefined,
            disableFocusStyles: disableFocusStyles
          })
        }))
      });
    }
  });
};
export const DateInputStorybook = DateInputComponent;
export const DateInput = ShareInput(Controllable(UsesToday( /*#__PURE__*/memo(DateInputComponent)), ['calendarPage', 'open', 'value', 'focusedValue']));
DateInput.displayName = 'DateInput';